import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api as API } from "../App";

const statusColor = {
  "In Progress": "var(--c)", "Active": "var(--c)", "Pending": "var(--a)",
  "Review": "var(--a)", "Briefing": "var(--a)", "Asset Live": "var(--g)",
  "Won": "var(--g)", "Completed": "var(--g)", "Cancelled": "#666", "Lost": "#666",
};
const funnelColors = {
  "Lead": "#8b5cf6", "Prospect": "var(--c)", "Proposal": "var(--a)",
  "Negotiation": "var(--r)", "Won": "var(--g)",
};

function Badge({ text, color }) {
  return (
    <span style={{
      background: (color||"var(--c)")+"22", color: color||"var(--c)",
      border: `1px solid ${color||"var(--c)"}44`,
      borderRadius: 4, padding: "2px 8px", fontSize: 11, fontWeight: 600,
      textTransform: "uppercase", letterSpacing: 0.5,
    }}>{text}</span>
  );
}

function EmptySlot({ icon, text }) {
  return (
    <div style={{ textAlign:"center", padding:"32px 0", color:"#666", fontSize:13 }}>
      <div style={{ fontSize:28, marginBottom:8 }}>{icon}</div>{text}
    </div>
  );
}

function SectionCard({ title, icon, count, children, accent="var(--c)" }) {
  return (
    <div style={{ background:"var(--surface)", border:"1px solid #1e1e2e", borderRadius:12, overflow:"hidden" }}>
      <div style={{ padding:"14px 18px", borderBottom:"1px solid #1a1a2a", display:"flex", alignItems:"center", gap:10 }}>
        <span style={{ fontSize:18 }}>{icon}</span>
        <span style={{ fontWeight:700, fontSize:15, color:"var(--t1)" }}>{title}</span>
        {count > 0 && (
          <span style={{ marginLeft:"auto", background:accent+"22", color:accent, border:`1px solid ${accent}44`, borderRadius:20, padding:"2px 10px", fontSize:12, fontWeight:700 }}>{count}</span>
        )}
      </div>
      <div style={{ padding:"8px 0" }}>{children}</div>
    </div>
  );
}

function TaskRow({ task }) {
  const isOverdue = task.due_date && new Date(task.due_date) < new Date();
  return (
    <div style={{ padding:"10px 18px", borderBottom:"1px solid #111120", display:"flex", alignItems:"flex-start", gap:12 }}>
      <div style={{ width:8, height:8, borderRadius:"50%", background:isOverdue?"var(--r)":"var(--c)", marginTop:6, flexShrink:0 }} />
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ fontWeight:600, fontSize:13, color:"var(--t1)", marginBottom:2 }}>{task.title||task.task_name||task.name||"Untitled task"}</div>
        {task.project_name && <div style={{ fontSize:11, color:"#666" }}>{task.project_name}</div>}
      </div>
      <div style={{ flexShrink:0, textAlign:"right" }}>
        {task.due_date && <div style={{ fontSize:11, color:isOverdue?"var(--r)":"#888", fontWeight:isOverdue?700:400 }}>{isOverdue?"⚠ ":""}{new Date(task.due_date).toLocaleDateString("en-IN",{day:"numeric",month:"short"})}</div>}
        {task.priority && <Badge text={task.priority} color={task.priority==="High"?"var(--r)":task.priority==="Medium"?"var(--a)":"#666"} />}
      </div>
    </div>
  );
}

function BriefRow({ brief }) {
  const color = statusColor[brief.status]||"var(--c)";
  return (
    <div style={{ padding:"10px 18px", borderBottom:"1px solid #111120", display:"flex", alignItems:"flex-start", gap:12 }}>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ fontWeight:600, fontSize:13, color:"var(--t1)", marginBottom:3 }}>{brief.brief_name||"Untitled brief"}</div>
        {brief.client_name && <div style={{ fontSize:11, color:"#888" }}>{brief.client_name}</div>}
      </div>
      <div style={{ flexShrink:0, textAlign:"right", display:"flex", flexDirection:"column", alignItems:"flex-end", gap:4 }}>
        <Badge text={brief.status||"Active"} color={color} />
        {brief.budget > 0 && <div style={{ fontSize:11, color:"#888" }}>₹{(brief.budget/100000).toFixed(1)}L</div>}
      </div>
    </div>
  );
}

function LeadRow({ lead, onClick }) {
  const color = funnelColors[lead.funnel_stage]||"var(--c)";
  return (
    <div onClick={onClick} style={{ padding:"10px 18px", borderBottom:"1px solid #111120", display:"flex", alignItems:"flex-start", gap:12, cursor:"pointer" }}
      onMouseEnter={e=>e.currentTarget.style.background="#0d0d1a"} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ fontWeight:600, fontSize:13, color:"var(--t1)", marginBottom:3 }}>{lead.company_name||"Unknown"}</div>
        {lead.contact_name && <div style={{ fontSize:11, color:"#888" }}>{lead.contact_name}</div>}
      </div>
      <div style={{ flexShrink:0, textAlign:"right", display:"flex", flexDirection:"column", alignItems:"flex-end", gap:4 }}>
        <Badge text={lead.funnel_stage||"Lead"} color={color} />
        {lead.estimated_value > 0 && <div style={{ fontSize:11, color:"#888" }}>₹{(lead.estimated_value/100000).toFixed(1)}L</div>}
        {lead.next_follow_up && <div style={{ fontSize:10, color:new Date(lead.next_follow_up)<new Date()?"var(--r)":"#666" }}>Follow up: {new Date(lead.next_follow_up).toLocaleDateString("en-IN",{day:"numeric",month:"short"})}</div>}
      </div>
    </div>
  );
}

function ProjectRow({ project }) {
  const color = statusColor[project.status]||"var(--c)";
  return (
    <div style={{ padding:"10px 18px", borderBottom:"1px solid #111120", display:"flex", alignItems:"flex-start", gap:12 }}>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ fontWeight:600, fontSize:13, color:"var(--t1)", marginBottom:3 }}>{project.project_name||project.name||"Untitled"}</div>
        {project.client_name && <div style={{ fontSize:11, color:"#888" }}>{project.client_name}</div>}
      </div>
      <div style={{ flexShrink:0, textAlign:"right", display:"flex", flexDirection:"column", alignItems:"flex-end", gap:4 }}>
        <Badge text={project.status||"Active"} color={color} />
        {project.due_date && <div style={{ fontSize:11, color:"#888" }}>Due {new Date(project.due_date).toLocaleDateString("en-IN",{day:"numeric",month:"short"})}</div>}
      </div>
    </div>
  );
}

const roleLabels = { Admin:"Admin", GAM:"Group Account Manager", ECD:"Executive Creative Director", VIP:"VIP", Manager:"Manager", SrManager:"Senior Manager", Executive:"Executive", Associate:"Associate", hr:"HR" };

export default function ProfilePage() {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    const token = localStorage.getItem("dreamteam_token");
    if (!token) { navigate("/login"); return; }
    API.get("/me/dashboard").then(r => setDashboard(r.data)).catch(console.error).finally(() => setLoading(false));
  }, []);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  if (loading) return <div style={{ display:"flex", justifyContent:"center", alignItems:"center", height:"60vh", color:"var(--c)", fontSize:14 }}>Loading your dashboard…</div>;

  const user = dashboard?.user || {};
  const tasks = dashboard?.tasks || [];
  const briefs = dashboard?.briefs || [];
  const leads = dashboard?.leads || [];
  const projects = dashboard?.projects || [];
  const overdueTasks = tasks.filter(t => t.due_date && new Date(t.due_date) < new Date());
  const overdueLeads = leads.filter(l => l.next_follow_up && new Date(l.next_follow_up) < new Date());

  const tabs = [
    { id:"overview", label:"Overview" },
    { id:"tasks", label:`Tasks${tasks.length?` (${tasks.length})`:""}` },
    { id:"briefs", label:`Briefs${briefs.length?` (${briefs.length})`:""}` },
    { id:"leads", label:`Leads${leads.length?` (${leads.length})`:""}` },
    { id:"projects", label:`Projects${projects.length?` (${projects.length})`:""}` },
  ];

  return (
    <div className="dm-page-container" style={{ padding:"24px 28px", maxWidth:1000, margin:"0 auto" }}>
      {/* Header */}
      <div className="flex flex-wrap" style={{ background:"var(--surface)", border:"1px solid #1e1e2e", borderRadius:16, padding:"24px 28px", marginBottom:24, alignItems:"center", gap:20 }}>
        <div style={{ width:64, height:64, borderRadius:"50%", background:"linear-gradient(135deg, var(--r), var(--c))", display:"flex", alignItems:"center", justifyContent:"center", fontSize:26, fontWeight:700, color:"#fff", flexShrink:0 }}>
          {user.name ? user.name[0].toUpperCase() : "?"}
        </div>
        <div style={{ flex:1 }}>
          <h1 style={{ margin:0, fontSize:22, fontWeight:800, color:"var(--t1)" }}>{user.name||"Your Dashboard"}</h1>
          <div style={{ display:"flex", alignItems:"center", gap:10, marginTop:6, flexWrap:"wrap" }}>
            {user.role && <Badge text={roleLabels[user.role]||user.role} color="var(--c)" />}
            {user.department && <span style={{ fontSize:12, color:"#888" }}>{user.department}</span>}
            {user.email && <span style={{ fontSize:12, color:"#555" }}>{user.email}</span>}
          </div>
        </div>
        {overdueTasks.length > 0 && (
          <div style={{ background:"var(--r)22", border:"1px solid var(--r)44", borderRadius:8, padding:"8px 14px", textAlign:"center" }}>
            <div style={{ fontSize:20, fontWeight:800, color:"var(--r)" }}>{overdueTasks.length}</div>
            <div style={{ fontSize:10, color:"var(--r)", textTransform:"uppercase", letterSpacing:0.5 }}>Overdue</div>
          </div>
        )}
        {overdueLeads.length > 0 && (
          <div style={{ background:"var(--a)22", border:"1px solid var(--a)44", borderRadius:8, padding:"8px 14px", textAlign:"center" }}>
            <div style={{ fontSize:20, fontWeight:800, color:"var(--a)" }}>{overdueLeads.length}</div>
            <div style={{ fontSize:10, color:"var(--a)", textTransform:"uppercase", letterSpacing:0.5 }}>Follow ups</div>
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="dm-profile-grid" style={{ display:"grid", gridTemplateColumns:"repeat(4, 1fr)", gap:12, marginBottom:24 }}>
        {[
          { label:"Active Tasks", value:tasks.length, color:"var(--c)" },
          { label:"Active Briefs", value:briefs.length, color:"var(--a)" },
          { label:"Leads to Chase", value:leads.length, color:"#8b5cf6" },
          { label:"Projects", value:projects.length, color:"var(--g)" },
        ].map(s => (
          <div key={s.label} style={{ background:"var(--surface)", border:"1px solid #1e1e2e", borderRadius:10, padding:"16px 18px", borderTop:`3px solid ${s.color}` }}>
            <div style={{ fontSize:26, fontWeight:800, color:s.color }}>{s.value}</div>
            <div style={{ fontSize:12, color:"#888", marginTop:4 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap" style={{ gap:4, marginBottom:16, borderBottom:"1px solid #1a1a2a" }}>
        {tabs.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{ background:"none", border:"none", cursor:"pointer", padding:"8px 16px", fontSize:13, fontWeight:600, color:activeTab===tab.id?"var(--c)":"#666", borderBottom:activeTab===tab.id?"2px solid var(--c)":"2px solid transparent", marginBottom:-1 }}>{tab.label}</button>
        ))}
      </div>

      {activeTab === "overview" && (
        <div className="dm-profile-2col" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
          <SectionCard title="My Tasks" icon="✓" count={tasks.length} accent="var(--c)">
            {tasks.length === 0 ? <EmptySlot icon="🎉" text="No pending tasks" /> : tasks.slice(0,5).map((t,i) => <TaskRow key={i} task={t} />)}
            {tasks.length > 5 && <div onClick={() => setActiveTab("tasks")} style={{ padding:"10px 18px", fontSize:12, color:"var(--c)", cursor:"pointer" }}>View all {tasks.length} →</div>}
          </SectionCard>
          <SectionCard title="Leads to Chase" icon="🎯" count={leads.length} accent="#8b5cf6">
            {leads.length === 0 ? <EmptySlot icon="📭" text="No active leads assigned" /> : leads.slice(0,5).map((l,i) => <LeadRow key={i} lead={l} onClick={() => navigate("/bizdev/leads")} />)}
            {leads.length > 5 && <div onClick={() => setActiveTab("leads")} style={{ padding:"10px 18px", fontSize:12, color:"#8b5cf6", cursor:"pointer" }}>View all {leads.length} →</div>}
          </SectionCard>
          <SectionCard title="Active Briefs" icon="📋" count={briefs.length} accent="var(--a)">
            {briefs.length === 0 ? <EmptySlot icon="📝" text="No active briefs" /> : briefs.slice(0,5).map((b,i) => <BriefRow key={i} brief={b} />)}
            {briefs.length > 5 && <div onClick={() => setActiveTab("briefs")} style={{ padding:"10px 18px", fontSize:12, color:"var(--a)", cursor:"pointer" }}>View all {briefs.length} →</div>}
          </SectionCard>
          <SectionCard title="My Projects" icon="🚀" count={projects.length} accent="var(--g)">
            {projects.length === 0 ? <EmptySlot icon="📦" text="No active projects" /> : projects.slice(0,5).map((p,i) => <ProjectRow key={i} project={p} />)}
            {projects.length > 5 && <div onClick={() => setActiveTab("projects")} style={{ padding:"10px 18px", fontSize:12, color:"var(--g)", cursor:"pointer" }}>View all {projects.length} →</div>}
          </SectionCard>
        </div>
      )}
      {activeTab === "tasks" && <SectionCard title="All My Tasks" icon="✓" count={tasks.length} accent="var(--c)">{tasks.length === 0 ? <EmptySlot icon="🎉" text="No pending tasks!" /> : tasks.map((t,i) => <TaskRow key={i} task={t} />)}</SectionCard>}
      {activeTab === "briefs" && <SectionCard title="All My Briefs" icon="📋" count={briefs.length} accent="var(--a)">{briefs.length === 0 ? <EmptySlot icon="📝" text="No active briefs" /> : briefs.map((b,i) => <BriefRow key={i} brief={b} />)}</SectionCard>}
      {activeTab === "leads" && <SectionCard title="My Leads" icon="🎯" count={leads.length} accent="#8b5cf6">{leads.length === 0 ? <EmptySlot icon="📭" text="No active leads" /> : leads.map((l,i) => <LeadRow key={i} lead={l} onClick={() => navigate("/bizdev/leads")} />)}</SectionCard>}
      {activeTab === "projects" && <SectionCard title="My Projects" icon="🚀" count={projects.length} accent="var(--g)">{projects.length === 0 ? <EmptySlot icon="📦" text="No active projects" /> : projects.map((p,i) => <ProjectRow key={i} project={p} />)}</SectionCard>}
    </div>
  );
}
